import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Table,
  Button,
  Input,
  Select,
  Space,
  Card,
  Tag,
  Tooltip,
  Modal,
  message,
  Row,
  Col,
  Statistic,
  Badge,
  Dropdown,
  Menu,
  Typography,
  Divider,
  Switch,
  DatePicker,
  Alert,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  PlusOutlined,
  SearchOutlined,
  FilterOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  SettingOutlined,
  GlobalOutlined,
  CalendarOutlined,
  DollarOutlined,
  BankOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { HRTaxLegislation, TaxLegislationFilters } from "../../types/hr.d";
import TaxLegislationForm from "./TaxLegislationForm";
import TaxLegislationDetails from "./TaxLegislationDetails";
import { useAuth } from "../../context/AuthContext";
import { useSelection } from "../../SelectionContext";
import useBusinessResource from "../../hooks/useBusinessResource";

const { Search } = Input;
const { Option } = Select;
const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

interface TaxLegislationListProps {
  // Props pour l'intégration avec le contexte parent
}

const TaxLegislationList: React.FC<TaxLegislationListProps> = () => {
  // États locaux
  const [legislations, setLegislations] = useState<HRTaxLegislation[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedLegislation, setSelectedLegislation] =
    useState<HRTaxLegislation | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [editingLegislation, setEditingLegislation] =
    useState<HRTaxLegislation | null>(null);
  const [filters, setFilters] = useState<TaxLegislationFilters>({});
  const [showFilters, setShowFilters] = useState(false);
  const { user, token } = useAuth();
  const { selectedCompany } = useSelection();
  const [editingId, setEditingId] = useState<number | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const detailSignature = useRef<string | null>(null);
  const details = useBusinessResource({
    userId: user?.id, companyId: selectedCompany || "global", warehouseId: "company", moduleScope: "hr",
    resource: "payroll-legislation-details", parameters: [editingId], enabled: Boolean(token && editingId), active: Boolean(editingId),
    load: async signal => {
      const response = await fetch(`/api/hr/tax-legislations/${editingId}`, { signal,
        headers: { Authorization: `Bearer ${token}`, ...(selectedCompany ? { "X-Company-Id": String(selectedCompany) } : {}) },
      });
      const result = await response.json();
      if (!response.ok || !result.success || Number(result.data?.id) !== Number(editingId)) throw new Error(result.message || result.error || "Impossible de lire toutes les règles de cette législation");
      return result.data as HRTaxLegislation;
    },
  });
  useEffect(() => {
    if (!editingId) return;
    if (details.error) { setDetailError(details.error instanceof Error ? details.error.message : "Lecture des règles impossible"); return; }
    if (details.status !== "verified" || !details.data) return;
    const signature = JSON.stringify(details.data);
    if (detailSignature.current && detailSignature.current !== signature) {
      setDetailError("Cette législation a changé pendant la saisie. Vos champs sont conservés. Fermez puis rouvrez le formulaire pour vérifier toutes les règles avant de sauvegarder."); return;
    }
    if (!detailSignature.current) {
      detailSignature.current = signature; setEditingLegislation(details.data); setShowForm(true);
    }
    setDetailError(null);
  }, [editingId, details.data, details.status, details.error]);


  // Chargement des données
  useEffect(() => {
    fetchLegislations();
  }, []);

  const fetchLegislations = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(
        "/api/hr/tax-legislations?with_details=true",
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            ...(selectedCompany ? { "X-Company-Id": String(selectedCompany) } : {}),
          },
        }
      );
      const data = await response.json();

      if (data.success) {
        setLegislations(data.data);
      } else {
        message.error(
          data.message || "Erreur lors du chargement des législations"
        );
      }
    } catch (error) {
      console.error("Erreur:", error);
      message.error("Erreur lors du chargement des législations");
    } finally {
      setLoading(false);
    }
  };

  // Données filtrées
  const filteredLegislations = useMemo(() => {
    return legislations.filter((legislation) => {
      if (
        filters.search &&
        !legislation.country_name
          .toLowerCase()
          .includes(filters.search.toLowerCase()) &&
        !legislation.country_code
          .toLowerCase()
          .includes(filters.search.toLowerCase())
      ) {
        return false;
      }

      if (
        filters.country_code &&
        legislation.country_code !== filters.country_code
      ) {
        return false;
      }

      if (
        filters.is_active !== undefined &&
        legislation.is_active !== filters.is_active
      ) {
        return false;
      }

      if (
        filters.is_default !== undefined &&
        legislation.is_default !== filters.is_default
      ) {
        return false;
      }

      return true;
    });
  }, [legislations, filters]);

  // Statistiques
  const statistics = useMemo(() => {
    const total = legislations.length;
    const active = legislations.filter((l) => l.is_active).length;
    const defaultLegislation = legislations.find((l) => l.is_default);
    const countries = new Set(legislations.map((l) => l.country_code)).size;

    return {
      total,
      active,
      inactive: total - active,
      countries,
      defaultLegislation: defaultLegislation?.country_name || "Aucune",
    };
  }, [legislations]);

  // Gestionnaires d'événements
  const handleCreate = () => {
    setEditingId(null); detailSignature.current = null; setDetailError(null);
    setEditingLegislation(null);
    setShowForm(true);
  };

  const handleEdit = (legislation: HRTaxLegislation) => {
    detailSignature.current = null; setDetailError(null); setEditingLegislation(null); setShowForm(false);
    if (editingId === legislation.id) void details.refresh({ force: true }).catch(() => {});
    else setEditingId(legislation.id);
  };

  const handleView = (legislation: HRTaxLegislation) => {
    setSelectedLegislation(legislation);
    setShowDetails(true);
  };

  const handleDelete = (legislation: HRTaxLegislation) => {
    if (legislation.is_default) {
      message.error("Impossible de supprimer la législation par défaut");
      return;
    }

    Modal.confirm({
      title: "Confirmer la suppression",
      content: `Êtes-vous sûr de vouloir supprimer la législation "${legislation.country_name}" ?`,
      icon: <ExclamationCircleOutlined />,
      okText: "Supprimer",
      cancelText: "Annuler",
      okType: "danger",
      onOk: async () => {
        try {
          const token = localStorage.getItem("authToken");
          const response = await fetch(
            `/api/hr/tax-legislations/${legislation.id}`,
            {
              method: "DELETE",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );

          const data = await response.json();

          if (data.success) {
            message.success("Législation supprimée avec succès");
            fetchLegislations();
          } else {
            message.error(data.message || "Erreur lors de la suppression");
          }
        } catch (error) {
          console.error("Erreur:", error);
          message.error("Erreur lors de la suppression");
        }
      },
    });
  };

  const handleToggleActive = async (legislation: HRTaxLegislation) => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(
        `/api/hr/tax-legislations/${legislation.id}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            ...(selectedCompany ? { "X-Company-Id": String(selectedCompany) } : {}),
          },
          body: JSON.stringify({
            ...Object.fromEntries(['country_code', 'country_name', 'currency_code', 'currency_symbol', 'is_default', 'effective_date', 'description']
              .map(key => [key, legislation[key as keyof HRTaxLegislation]])),
            is_active: !legislation.is_active,
          }),
        }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        message.success(
          `Législation ${
            !legislation.is_active ? "activée" : "désactivée"
          } avec succès`
        );
        fetchLegislations();
      } else {
        message.error(data.message || "Erreur lors de la mise à jour");
      }
    } catch (error) {
      console.error("Erreur:", error);
      message.error("Erreur lors de la mise à jour");
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false); setEditingId(null); detailSignature.current = null; setDetailError(null);
    setEditingLegislation(null);
    fetchLegislations();
  };

  const handleResetFilters = () => {
    setFilters({});
  };

  // Colonnes du tableau
  const columns: ColumnsType<HRTaxLegislation> = [
    {
      title: "Pays",
      key: "country",
      width: 200,
      render: (_, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <GlobalOutlined style={{ color: "#1890ff" }} />
          <div>
            <div style={{ fontWeight: 500 }}>{record.country_name}</div>
            <Text type="secondary" style={{ fontSize: "12px" }}>
              {record.country_code}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: "Devise",
      key: "currency",
      width: 100,
      render: (_, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <DollarOutlined style={{ color: "#52c41a" }} />
          <span>{record.currency_symbol}</span>
          <Text type="secondary" style={{ fontSize: "12px" }}>
            ({record.currency_code})
          </Text>
        </div>
      ),
    },
    {
      title: "Date effective",
      dataIndex: "effective_date",
      key: "effective_date",
      width: 120,
      render: (date) => (
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <CalendarOutlined style={{ color: "#faad14" }} />
          <span>{new Date(date).toLocaleDateString("fr-FR")}</span>
        </div>
      ),
    },
    {
      title: "Éléments configurés",
      key: "elements",
      width: 200,
      render: (_, record) => (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Badge count={record.social_contributions_count || 0} size="small">
            <Tag color="blue">Cotisations</Tag>
          </Badge>
          <Badge count={record.tax_brackets_count || 0} size="small">
            <Tag color="orange">Tranches</Tag>
          </Badge>
          <Badge count={record.tax_allowances_count || 0} size="small">
            <Tag color="green">Abattements</Tag>
          </Badge>
          <Badge count={record.parameters_count || 0} size="small">
            <Tag color="purple">Paramètres</Tag>
          </Badge>
        </div>
      ),
    },
    {
      title: "Statut",
      key: "status",
      width: 120,
      render: (_, record) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <Switch
            checked={record.is_active}
            onChange={() => handleToggleActive(record)}
            size="small"
            checkedChildren="Actif"
            unCheckedChildren="Inactif"
          />
          {record.is_default && (
            <Tag color="gold" style={{ fontSize: "10px" }}>
              PAR DÉFAUT
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Dropdown
          overlay={
            <Menu>
              <Menu.Item
                key="view"
                icon={<EyeOutlined />}
                onClick={() => handleView(record)}
              >
                Voir détails
              </Menu.Item>
              <Menu.Item
                key="edit"
                icon={<EditOutlined />}
                onClick={() => handleEdit(record)}
              >
                Modifier
              </Menu.Item>
              <Menu.Divider />
              <Menu.Item
                key="delete"
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(record)}
                disabled={record.is_default}
                danger
              >
                Supprimer
              </Menu.Item>
            </Menu>
          }
          trigger={["click"]}
        >
          <Button type="text" icon={<SettingOutlined />} />
        </Dropdown>
      ),
    },
  ];

  return (
    <div style={{ padding: "24px" }}>
      {/* En-tête avec statistiques */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16}>
          <Col span={6}>
            <Statistic
              title="Total législations"
              value={statistics.total}
              prefix={<GlobalOutlined />}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Actives"
              value={statistics.active}
              prefix={<CheckCircleOutlined style={{ color: "#52c41a" }} />}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Pays couverts"
              value={statistics.countries}
              prefix={<BankOutlined />}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Législation par défaut"
              value={statistics.defaultLegislation}
              prefix={
                <ExclamationCircleOutlined style={{ color: "#faad14" }} />
              }
            />
          </Col>
        </Row>
      </Card>

      {/* Barre d'actions */}
      <Card style={{ marginBottom: 24 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={4} style={{ margin: 0 }}>
              Législations fiscales
            </Title>
            <Text type="secondary">
              Gestion des législations fiscales par pays
            </Text>
          </Col>
          <Col>
            <Space>
              <Button
                icon={<FilterOutlined />}
                onClick={() => setShowFilters(!showFilters)}
                type={showFilters ? "primary" : "default"}
              >
                Filtres
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreate}
              >
                Nouvelle législation
              </Button>
            </Space>
          </Col>
        </Row>

        {/* Filtres */}
        {showFilters && (
          <>
            <Divider />
            <Row gutter={16}>
              <Col span={6}>
                <Search
                  placeholder="Rechercher par pays..."
                  value={filters.search}
                  onChange={(e) =>
                    setFilters({ ...filters, search: e.target.value })
                  }
                  allowClear
                />
              </Col>
              <Col span={4}>
                <Select
                  placeholder="Code pays"
                  value={filters.country_code}
                  onChange={(value) =>
                    setFilters({ ...filters, country_code: value })
                  }
                  allowClear
                  style={{ width: "100%" }}
                >
                  {Array.from(
                    new Set(legislations.map((l) => l.country_code))
                  ).map((code) => (
                    <Option key={code} value={code}>
                      {code}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col span={4}>
                <Select
                  placeholder="Statut"
                  value={filters.is_active}
                  onChange={(value) =>
                    setFilters({ ...filters, is_active: value })
                  }
                  allowClear
                  style={{ width: "100%" }}
                >
                  <Option value={true}>Actif</Option>
                  <Option value={false}>Inactif</Option>
                </Select>
              </Col>
              <Col span={4}>
                <Select
                  placeholder="Type"
                  value={filters.is_default}
                  onChange={(value) =>
                    setFilters({ ...filters, is_default: value })
                  }
                  allowClear
                  style={{ width: "100%" }}
                >
                  <Option value={true}>Par défaut</Option>
                  <Option value={false}>Standard</Option>
                </Select>
              </Col>
              <Col span={4}>
                <Button onClick={handleResetFilters}>Réinitialiser</Button>
              </Col>
            </Row>
          </>
        )}
      </Card>

      {/* Alerte si aucune législation par défaut */}
      {!statistics.defaultLegislation && (
        <Alert
          message="Aucune législation par défaut"
          description="Il est recommandé de définir une législation par défaut pour les calculs de paie."
          type="warning"
          showIcon
          style={{ marginBottom: 24 }}
        />
      )}

      {detailError && <Alert type="error" showIcon message="Édition de législation bloquée" description={detailError}
        action={<Button onClick={() => { void details.refresh({ force: true }).catch(() => {}); }}>Réessayer</Button>} style={{ marginBottom: 16 }} />}
      {/* Tableau */}
      <Card>
        <Table
          columns={columns}
          dataSource={filteredLegislations}
          rowKey="id"
          loading={loading}
          pagination={{
            total: filteredLegislations.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} sur ${total} législations`,
          }}
          scroll={{ x: 1000 }}
        />
      </Card>

      {/* Modals */}
      {showForm && (
        <TaxLegislationForm
          open={showForm}
          onClose={() => { setShowForm(false); setEditingId(null); detailSignature.current = null; setDetailError(null); }}
          disabled={Boolean(editingId && (details.status !== 'verified' || detailError))}
          companyId={selectedCompany || undefined}
          errorMessage={detailError}
          onSuccess={handleFormSuccess}
          legislation={editingLegislation}
        />
      )}

      {showDetails && selectedLegislation && (
        <TaxLegislationDetails
          open={showDetails}
          onClose={() => setShowDetails(false)}
          legislation={selectedLegislation}
        />
      )}
    </div>
  );
};

export default TaxLegislationList;
